import type { Card } from '@tctp/protocol';
import { useEffect, useRef } from 'react';
import { boardDealDelays } from './stage';

/**
 * Deal delay per board card, fixed the first time each card appears so later renders never replay its animation.
 * `fresh` holds the delays assigned in this render (empty when nothing new was dealt); `extraDelayMs` is added to those only.
 * The delays are recorded after commit, so StrictMode's double render sees the same `fresh` twice.
 * A hand first seen with cards already on the board (a reload or a join mid-hand) was dealt before this client looked: nothing is fresh.
 */
export function useBoardDeal(handId: string | null, board: readonly Card[], extraDelayMs = 0): { delays: number[]; fresh: number[] } {
  const seen = useRef<{ handId: string | null; delays: Map<Card, number> }>({ handId: null, delays: new Map() });
  if (seen.current.handId !== handId) seen.current = { handId, delays: new Map(board.map(c => [c, 0])) };
  const m = seen.current.delays;
  const unseen = board.filter(c => !m.has(c));
  const fresh = boardDealDelays(m.size, board.length).map(d => d + extraDelayMs);
  useEffect(() => { unseen.forEach((c, k) => m.set(c, fresh[k] ?? 0)); });
  return { delays: board.map(c => m.get(c) ?? fresh[unseen.indexOf(c)] ?? 0), fresh };
}
